// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useEffect, useMemo, useState } from 'react';
import { intelligenceAdminApi, type ChatUseCaseDefault } from '../api/intelligenceAdminApi';
import DefaultsForm from '../components/intelligence/DefaultsForm';
import OverridesTable from '../components/intelligence/OverridesTable';

type Tab = 'defaults' | 'overrides';

const IntelligenceConfigPage: React.FC = () => {
  const [useCases, setUseCases] = useState<ChatUseCaseDefault[]>([]);
  const [selectedCode, setSelectedCode] = useState<string>('FACT_FIND');
  const [tab, setTab] = useState<Tab>('defaults');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await intelligenceAdminApi.listUseCases();
      setUseCases(list);
      if (list.length > 0 && !list.find(u => u.code === selectedCode)) {
        setSelectedCode(list[0].code);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load use cases');
    } finally {
      setLoading(false);
    }
  };

  // Initial load only — reload is intentionally not in deps to avoid an
  // infinite re-render (it depends on selectedCode which changes via the
  // picker).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);

  const selected = useMemo(() => useCases.find(u => u.code === selectedCode), [useCases, selectedCode]);

  const onDefaultsSaved = (next: ChatUseCaseDefault) => {
    setUseCases(prev => prev.map(u => u.code === next.code ? next : u));
  };

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: 24 }} data-testid="intelligence-config-page">
      <h1 style={{ marginTop: 0 }}>Intelligence configuration</h1>
      <p style={{ color: '#6b7280' }}>
        Configure the conversational AI use cases. The "Defaults" tab edits the platform-wide
        baseline; the "Brokerage overrides" tab edits per-tenant exceptions.
      </p>

      {error && <div style={{ color: '#b91c1c', marginBottom: 8 }}>{error}</div>}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
        <label>
          Use case
          <select value={selectedCode} onChange={(e) => setSelectedCode(e.target.value)}
            style={{ marginLeft: 8, padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4 }}
            data-testid="usecase-select">
            {useCases.map(u => (
              <option key={u.code} value={u.code}>
                {u.displayName} ({u.code}){u.enabled ? '' : ' — disabled'}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div style={{ display: 'flex', gap: 0, marginTop: 24, borderBottom: '1px solid #e5e7eb' }}>
        <button onClick={() => setTab('defaults')} style={tabStyle(tab === 'defaults')} data-testid="tab-defaults">
          Defaults
        </button>
        <button onClick={() => setTab('overrides')} style={tabStyle(tab === 'overrides')} data-testid="tab-overrides">
          Brokerage overrides
        </button>
      </div>

      <div style={{ marginTop: 16 }}>
        {loading || !selected ? (
          <div>Loading…</div>
        ) : tab === 'defaults' ? (
          <DefaultsForm useCase={selected} onSaved={onDefaultsSaved}/>
        ) : (
          <OverridesTable useCase={selected}/>
        )}
      </div>
    </div>
  );
};

const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: '10px 16px', border: 'none', background: 'transparent',
  borderBottom: active ? '2px solid #1e40af' : '2px solid transparent',
  fontWeight: active ? 600 : 400, cursor: 'pointer',
});

export default IntelligenceConfigPage;
